'use client';
import { useTranslations } from 'next-intl';
import { type FC, useEffect, useState } from 'react';
import { Button } from '@/app/components/ui/button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/app/components/ui/dialog';
import { Input } from '@/app/components/ui/input';
import { toast } from '@/app/components/ui/use-toast';
import { useStore } from '@/app/providers/store';
import { cn, reportEvent } from '@/app/utils';
import { usePathname } from '@/i18n/routing';
import { INFLUENCER_CODE_TEXT } from '../constants';
import { useValidateBonusCode } from '../hook/use-validate-bonus-code';

interface BonusCodeDialogProps {
  open: boolean;
  onOpenChange?: (open: boolean) => void;
  bonusCode: string | null;
  initialCode?: string;
  initialError?: string;
  source?: 'input' | 'edit';
}

export const BonusCodeDialog: FC<BonusCodeDialogProps> = ({
  open,
  onOpenChange,
  bonusCode,
  initialCode = '',
  initialError = '',
  source = 'input',
}) => {
  const t = useTranslations();
  const pathname = usePathname();
  const updateBonusCode = useStore((state) => state.updateBonusCode);

  const [errorMsg, setErrorMsg] = useState<string>('');
  const [code, setCode] = useState('');

  const { validateBonusCode, validateBonusCodeLoading } = useValidateBonusCode({
    onValidate: (error) => {
      if (!error) {
        toast({
          type: 'success',
          message: INFLUENCER_CODE_TEXT.dialog.success,
        });
        reportEvent('apply_promo_code', {
          from_source: 'input',
          page_name: pathname,
        });
        setErrorMsg('');
        updateBonusCode(code);
        handleClose();
      } else {
        setErrorMsg(
          error === 'UserHasSubscriptions'
            ? t('bonusCode.dialog.errorUserHasSubscriptions')
            : INFLUENCER_CODE_TEXT.dialog.error,
        );
      }
    },
  });

  useEffect(() => {
    if (open) {
      setCode(initialCode || bonusCode || '');
      setErrorMsg(initialError);
      reportEvent('page_name', { cpp_code: 'cpp_code_input_popup' });
    }
  }, [open, initialCode, initialError, bonusCode]);

  const handleClickIdentityNext = () => {
    reportEvent('cpp_code_click', {
      vip_price_cpp_code: source,
      cpp_code_input_popup: 'submit',
      cpp_code_id: 'submit',
    });
    validateBonusCode(code);
  };

  const handleClose = () => {
    setCode('');
    setErrorMsg('');
    onOpenChange?.(false);
  };

  const handleInputFocus = () => {
    reportEvent('cpp_code_click', {
      vip_price_cpp_code: source,
      cpp_code_input_popup: 'input_focus',
      cpp_code_id: 'submit',
    });
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCode(e.target.value);
    if (errorMsg) {
      setErrorMsg('');
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="w-[560px]">
        <DialogHeader>
          <DialogTitle>{INFLUENCER_CODE_TEXT.dialog.title}</DialogTitle>
        </DialogHeader>
        <div className="flex flex-col gap-2">
          <Input
            placeholder={INFLUENCER_CODE_TEXT.dialog.placeholder}
            className={cn(
              'h-[48px] w-full rounded-8 border border-system-white12 bg-system-white08 py-[10px] text-sm text-system-white placeholder:text-system-white64',
              errorMsg && 'border-system-error',
            )}
            value={code}
            onFocus={handleInputFocus}
            onChange={handleInputChange}
            maxLength={100}
          />
          {errorMsg && <p className="text-left text-system-error text-xs">{errorMsg}</p>}
        </div>
        <DialogFooter>
          <Button
            variant="primary"
            loading={validateBonusCodeLoading}
            disabled={!code || validateBonusCodeLoading}
            className={cn('w-[120px]', !code && 'bg-system-neutral02 text-system-white24 hover:bg-system-neutral02')}
            onClick={handleClickIdentityNext}
          >
            {INFLUENCER_CODE_TEXT.dialog.submit}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
